// scheduling/components/IDDHistogram.jsx
// Pure CSS bar chart showing weekly hours density: PY actual (gray) vs CY planned (teal).
// Shares the same weekly x-axis and zoom as IDDTimeline. The gold overlay is the focus job's
// last year (the hovered row or bar, else the selected one; the page fetches it).

import { useState, useMemo, useRef, useCallback } from 'react';

// ── Date helpers (same as IDDTimeline) ─────────────────────────────────────

function getMonday(d) {
  const dt = new Date(d);
  const dow = dt.getDay();
  dt.setDate(dt.getDate() - (dow === 0 ? 6 : dow - 1));
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function addWeeks(d, n) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n * 7);
  return dt;
}

function weeksBetween(a, b) {
  return Math.round((b - a) / (7 * 86400000));
}

function fmtWeekLabel(d) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Parse a 'YYYY-MM-DD' (or ISO 'YYYY-MM-DDTHH:MM:SS.sssZ' from SQL DATE) string
// as LOCAL midnight. Plain `new Date(...)` parses date-only strings as UTC midnight,
// which in CST shifts back to Sun 19:00 — breaks getMonday and bucket placement.
function parseLocalDate(val) {
  if (!val) return null;
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  const d = new Date(val);
  return isNaN(d) ? null : d;
}

// Default window when no cycle cutoff is set: today through next FY end (April 30, ≥12wk out).
function calcDefaultWeeksAhead() {
  const today = new Date();
  let fyEnd = new Date(today.getFullYear(), 3, 30);
  if ((fyEnd - today) / (7 * 86400000) < 12) {
    fyEnd = new Date(today.getFullYear() + 1, 3, 30);
  }
  return Math.ceil((fyEnd - today) / (7 * 86400000));
}

const BASE_WEEK_W = 100;
const CHART_H = 120;

export default function IDDHistogram({ jobs, edits, getEffective, pyWeeklyData = [], zoom = 1, focusJobPYWeekly = [], externalScrollRef, onHorizontalScroll, aiSuggestions = {}, cutoffDate = null, windowMode = 'current' }) {
  const internalScrollRef = useRef(null);
  const scrollRef = externalScrollRef || internalScrollRef;
  const [collapsed, setCollapsed] = useState(false);
  const [tooltip, setTooltip] = useState(null);

  const weekW = Math.round(BASE_WEEK_W * zoom);

  // Window selection (mirrors IDDTimeline):
  //  - 'current' (default): today's Monday → cutoff. Focused on the actionable window.
  //  - 'year': (cutoff − 52 weeks) → cutoff. Captures full PY-shifted busy season.
  // Bucket arrays are sized to weeksAhead, so legend totals reducing those arrays
  // automatically clip to the visible window.
  const { originMonday, weeksAhead, cutoffWeekIdx } = useMemo(() => {
    const todayMon = getMonday(new Date());
    if (cutoffDate) {
      const cutoffMon = getMonday(parseLocalDate(cutoffDate));
      if (windowMode === 'year') {
        const origin = addWeeks(cutoffMon, -52);
        return { originMonday: origin, weeksAhead: 53, cutoffWeekIdx: 52 };
      }
      const wks = Math.max(4, Math.floor((cutoffMon - todayMon) / (7 * 86400000)) + 1);
      return { originMonday: todayMon, weeksAhead: wks, cutoffWeekIdx: wks - 1 };
    }
    return {
      originMonday: todayMon,
      weeksAhead: calcDefaultWeeksAhead(),
      cutoffWeekIdx: null,
    };
  }, [cutoffDate, windowMode]);

  const totalW = weeksAhead * weekW;

  // ── CY Budget Remaining distribution ─────────────────────────────────────────

  const cyWeekly = useMemo(() => {
    const weekHours = new Array(weeksAhead).fill(0);
    for (const job of jobs) {
      const startRaw = getEffective(job, 'startDeadline');
      const endRaw = getEffective(job, 'deadline');
      const remaining = getEffective(job, 'remainingHrs') ?? job.RemainingHours ?? 0;
      if (!startRaw || !endRaw || remaining <= 0) continue;

      const start = getMonday(parseLocalDate(startRaw));
      const end = getMonday(parseLocalDate(endRaw));
      let sIdx = weeksBetween(originMonday, start);
      let eIdx = weeksBetween(originMonday, end);
      if (eIdx < 0 || sIdx >= weeksAhead) continue;
      sIdx = Math.max(sIdx, 0);
      eIdx = Math.min(eIdx, weeksAhead - 1);
      const span = eIdx - sIdx + 1;
      const density = remaining / span;
      for (let w = sIdx; w <= eIdx; w++) weekHours[w] += density;
    }
    return weekHours;
  }, [jobs, edits, getEffective, originMonday]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── AI Suggested distribution (unaccepted suggestions only) ─────────

  const aiWeekly = useMemo(() => {
    const weekHours = new Array(weeksAhead).fill(0);
    for (const job of jobs) {
      const pe = job.PEJobIndex;
      const rec = aiSuggestions[pe];
      if (!rec || !rec.startdate || !rec.enddate) continue;
      // Skip if already accepted (edit matches AI)
      const e = edits[pe] || {};
      if (e.startDeadline === rec.startdate && e.deadline === rec.enddate) continue;

      const remaining = Number(getEffective(job, 'remainingHrs') ?? job.RemainingHours) || 0;
      if (remaining <= 0) continue;

      const start = getMonday(parseLocalDate(rec.startdate));
      const end = getMonday(parseLocalDate(rec.enddate));
      let sIdx = weeksBetween(originMonday, start);
      let eIdx = weeksBetween(originMonday, end);
      if (eIdx < 0 || sIdx >= weeksAhead) continue;
      sIdx = Math.max(sIdx, 0);
      eIdx = Math.min(eIdx, weeksAhead - 1);
      const span = eIdx - sIdx + 1;
      const density = remaining / span;
      for (let w = sIdx; w <= eIdx; w++) weekHours[w] += density;
    }
    return weekHours;
  }, [jobs, edits, getEffective, aiSuggestions, originMonday, weeksAhead]);

  // ── PY Actual WIP distribution (from API data) ──────────────────────────

  const pyWeekly = useMemo(() => {
    const weekHours = new Array(weeksAhead).fill(0);
    for (const row of pyWeeklyData) {
      const ws = parseLocalDate(row.WeekStart);
      if (!ws) continue;
      // Shift PY date forward 1 year to align with CY timeline
      const shifted = new Date(ws);
      shifted.setFullYear(shifted.getFullYear() + 1);
      const idx = weeksBetween(originMonday, getMonday(shifted));
      if (idx >= 0 && idx < weeksAhead) {
        weekHours[idx] += row.TotalHours || 0;
      }
    }
    return weekHours;
  }, [pyWeeklyData, originMonday, weeksAhead]);

  // ── The focus job's PY distribution (gold overlay) ───────────────────

  const focusJobWeekly = useMemo(() => {
    const weekHours = new Array(weeksAhead).fill(0);
    if (!focusJobPYWeekly || focusJobPYWeekly.length === 0) return weekHours;
    for (const row of focusJobPYWeekly) {
      const ws = parseLocalDate(row.WeekStart);
      if (!ws) continue;
      const shifted = new Date(ws);
      shifted.setFullYear(shifted.getFullYear() + 1);
      const idx = weeksBetween(originMonday, getMonday(shifted));
      if (idx >= 0 && idx < weeksAhead) {
        weekHours[idx] += row.TotalHours || 0;
      }
    }
    return weekHours;
  }, [focusJobPYWeekly, originMonday, weeksAhead]);

  // ── Max for scale ───────────────────────────────────────────────────

  const maxHours = useMemo(() => {
    let max = 10;
    for (let i = 0; i < weeksAhead; i++) {
      const cyTotal = cyWeekly[i] + aiWeekly[i]; // AI stacks on top of CY
      if (cyTotal > max) max = cyTotal;
      if (pyWeekly[i] > max) max = pyWeekly[i];
    }
    return max;
  }, [cyWeekly, aiWeekly, pyWeekly, weeksAhead]);

  // ── Week labels ─────────────────────────────────────────────────────

  const weeks = useMemo(() => {
    const arr = [];
    for (let i = 0; i < weeksAhead; i++) {
      arr.push({ monday: addWeeks(originMonday, i), idx: i });
    }
    return arr;
  }, [originMonday, weeksAhead]);

  // ── Tooltip ─────────────────────────────────────────────────────────

  const handleBarHover = useCallback((e, weekIdx) => {
    const mon = addWeeks(originMonday, weekIdx);
    const lines = [`Week of ${fmtWeekLabel(mon)}`, `PY Actual WIP: ${Math.round(pyWeekly[weekIdx] * 100) / 100}h`, `CY Budget Remaining: ${Math.round(cyWeekly[weekIdx] * 100) / 100}h`];
    if (aiWeekly[weekIdx] > 0) lines.push(`AI Suggested: ${Math.round(aiWeekly[weekIdx] * 100) / 100}h`);
    if (focusJobWeekly[weekIdx] > 0) lines.push(`Highlighted job, last year: ${Math.round(focusJobWeekly[weekIdx] * 100) / 100}h`);
    setTooltip({ x: e.clientX + 12, y: e.clientY - 60, content: lines.join('\n') });
  }, [originMonday, pyWeekly, cyWeekly, aiWeekly, focusJobWeekly]);

  const contentH = CHART_H + 16;

  return (
    <div className="idd-hist-root">
      <div
        className="idd-tl-collapsible"
        style={{ maxHeight: collapsed ? 0 : contentH, opacity: collapsed ? 0 : 1 }}
      >
        <div className="idd-hist-scroll" ref={scrollRef} onScroll={onHorizontalScroll}>
          <div className="idd-hist-body" style={{ width: totalW, height: CHART_H }}>
            {/* Grid lines */}
            {weeks.map(w => (
              <div key={w.idx} className="idd-hist-gridline" style={{ left: w.idx * weekW, height: CHART_H }} />
            ))}

            {/* Cycle-end marker (only when cutoffDate is set) */}
            {cutoffWeekIdx != null && (
              <div
                className="idd-tl-cycle-end"
                style={{ left: cutoffWeekIdx * weekW + weekW, height: CHART_H }}
                title="Cycle cutoff"
              />
            )}

            {/* PY bars (gray, behind) */}
            {weeks.map(w => {
              const h = pyWeekly[w.idx] > 0 ? (pyWeekly[w.idx] / maxHours) * (CHART_H - 4) : 0;
              return h > 0 ? (
                <div
                  key={`py-${w.idx}`}
                  className="idd-hist-bar idd-hist-bar--py"
                  style={{
                    left: w.idx * weekW + 2,
                    width: weekW / 2 - 3,
                    height: h,
                    bottom: 0,
                  }}
                  onMouseEnter={e => handleBarHover(e, w.idx)}
                  onMouseLeave={() => setTooltip(null)}
                />
              ) : null;
            })}

            {/* CY bars (teal, in front) */}
            {weeks.map(w => {
              const h = cyWeekly[w.idx] > 0 ? (cyWeekly[w.idx] / maxHours) * (CHART_H - 4) : 0;
              return h > 0 ? (
                <div
                  key={`cy-${w.idx}`}
                  className="idd-hist-bar idd-hist-bar--cy"
                  style={{
                    left: w.idx * weekW + weekW / 2,
                    width: weekW / 2 - 3,
                    height: h,
                    bottom: 0,
                  }}
                  onMouseEnter={e => handleBarHover(e, w.idx)}
                  onMouseLeave={() => setTooltip(null)}
                />
              ) : null;
            })}

            {/* AI Suggested bars (purple, stacked on top of CY) */}
            {weeks.map(w => {
              const aiH = aiWeekly[w.idx] > 0 ? (aiWeekly[w.idx] / maxHours) * (CHART_H - 4) : 0;
              const cyH = cyWeekly[w.idx] > 0 ? (cyWeekly[w.idx] / maxHours) * (CHART_H - 4) : 0;
              return aiH > 0 ? (
                <div
                  key={`ai-${w.idx}`}
                  className="idd-hist-bar idd-hist-bar--ai"
                  style={{
                    left: w.idx * weekW + weekW / 2,
                    width: weekW / 2 - 3,
                    height: aiH,
                    bottom: cyH,
                  }}
                  onMouseEnter={e => handleBarHover(e, w.idx)}
                  onMouseLeave={() => setTooltip(null)}
                />
              ) : null;
            })}

            {/* The focus job's PY overlay (gold, on the PY bar side) */}
            {weeks.map(w => {
              const h = focusJobWeekly[w.idx] > 0 ? (focusJobWeekly[w.idx] / maxHours) * (CHART_H - 4) : 0;
              return h > 0 ? (
                <div
                  key={`sel-${w.idx}`}
                  className="idd-hist-bar idd-hist-bar--selected"
                  style={{
                    left: w.idx * weekW + 2,
                    width: weekW / 2 - 3,
                    height: h,
                    bottom: 0,
                  }}
                  onMouseEnter={e => handleBarHover(e, w.idx)}
                  onMouseLeave={() => setTooltip(null)}
                />
              ) : null;
            })}
          </div>
        </div>
      </div>

      {/* Toggle */}
      <div className="idd-tl-toolbar">
        <button className="idd-tl-toggle" onClick={() => setCollapsed(c => !c)}>
          <span className={`idd-tl-chevron${collapsed ? ' idd-tl-chevron--down' : ''}`}>{'\u25B2'}</span>
          {collapsed ? 'Show Hours Distribution' : 'Hide Hours Distribution'}
        </button>
      </div>

      {/* Legend — totals come from the windowed bucket arrays (sized to weeksAhead).
          With the cutoff-anchored window (cutoff − 52wk → cutoff), the bucket arrays
          already capture one full annual cycle of PY-shifted data and exclude anything
          beyond the cutoff. So summing buckets gives totals that match the visible chart. */}
      {!collapsed && (
        <div className="idd-hist-legend">
          <span className="idd-hist-legend-item"><span className="idd-hist-swatch idd-hist-swatch--py" /> PY Actual WIP ({Math.round(pyWeekly.reduce((s, h) => s + h, 0))}h)</span>
          <span className="idd-hist-legend-item"><span className="idd-hist-swatch idd-hist-swatch--cy" /> CY Budget Remaining ({Math.round(cyWeekly.reduce((s, h) => s + h, 0))}h)</span>
          {Object.keys(aiSuggestions).length > 0 && (
            <span className="idd-hist-legend-item"><span className="idd-hist-swatch idd-hist-swatch--ai" /> AI Suggested ({Math.round(aiWeekly.reduce((s, h) => s + h, 0))}h)</span>
          )}
          {focusJobPYWeekly.length > 0 && (
            <span className="idd-hist-legend-item"><span className="idd-hist-swatch idd-hist-swatch--selected" /> Highlighted job, last year ({Math.round(focusJobWeekly.reduce((s, h) => s + h, 0))}h)</span>
          )}
          {windowMode === 'current' && cutoffDate && (
            <span
              className="idd-hist-legend-note"
              title="Totals reflect the current view (this week onward). Toggle 'Whole Year' above to include prior-year distribution."
            >
              Totals reflect current view ⓘ
            </span>
          )}
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div className="idd-tl-tooltip" style={{ left: tooltip.x, top: tooltip.y, whiteSpace: 'pre-line' }}>
          {tooltip.content}
        </div>
      )}
    </div>
  );
}
